/**
 * VideoOverlay (#91, redesigned 2026-05-03 from live-test feedback)
 *
 * Custom UI wrapping a YouTube IFrame embed. Native YouTube controls
 * (small, hard to find) are disabled; the elderly user gets:
 *  - Big "Done" button at the bottom-left (next to Pause/Play, all
 *    controls close together — feedback from Bas's screenshot)
 *  - Big Pause / Play button at the bottom-centre
 *  - Volume slider at the bottom-right
 *  - Dark control bar with an orange AmigoMio accent stripe so the
 *    bar stands out and is recognisable as part of our app
 *  - "TV frame" — black border around the video for visual focus
 *
 * Driven via the YouTube IFrame API (postMessage) — same approach as
 * MusicPlayer. The iframe size is forced to 100% via belt-and-
 * suspenders styling because YT.Player otherwise defaults to a
 * 640×360 pixel iframe centred inside the frame (Bas's screenshot
 * showed a small video lost in a big black frame).
 *
 * Behaviour during the call:
 *  - When videoOverlay is set, mic is suppressed (TabletApp.js).
 *  - When the user presses Done, the overlay closes and the mic
 *    resumes via the useEffect in TabletApp.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';

// AmigoMio orange — matches the Call button. Used for accents on the
// control bar so the user recognises this as our app.
const BRAND_ORANGE = '#E8845A';

let ytApiReadyPromise = null;
function loadYouTubeApi() {
  if (window.YT && window.YT.Player) return Promise.resolve();
  if (ytApiReadyPromise) return ytApiReadyPromise;
  ytApiReadyPromise = new Promise(resolve => {
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.body.appendChild(tag);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (prev) prev();
      resolve();
    };
  });
  return ytApiReadyPromise;
}

// #441 Phase 2 — VideoOverlay i18n bucket. Falls back to English
// when LanguageProvider isn't in the tree.
function _useStrings() {
  const ctx = useLang();
  const s = ctx && ctx.t && ctx.t.tablet && ctx.t.tablet.videooverlay;
  return {
    done: (s && s.done) || 'Done',
    play: (s && s.play) || 'Play',
    pause: (s && s.pause) || 'Pause',
    volumeDown: (s && s.volumeDown) || 'Volume down',
    volumeUp: (s && s.volumeUp) || 'Volume up',
    closeAria: (s && s.closeAria) || 'Close video',
  };
}

// #625 — how long to wait after onReady for the player to actually
// reach PLAYING (state=1) before treating the load as a silent rights-
// block. Most healthy embeds reach PLAYING within ~1-2 s of onReady;
// 5 s is a generous ceiling without making a real failure feel slow.
const NEVER_PLAYED_TIMEOUT_MS = 5000;

export default function VideoOverlay({ youtubeId, fallbackIds, title, onUnavailable }) {
  const tr = _useStrings();
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const readyRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(true); // assume autoplay starts
  const [volume, setVolume] = useState(80);
  // Mirror volume into a ref so the player-creation useEffect can read
  // the latest value in onReady without listing `volume` as a dependency.
  // Without this, the effect re-ran on every +/- click, destroying and
  // recreating the YT player — which restarted the video and reset the
  // iframe to its default 640×360 pixels (the "video shrinks and starts
  // over" bug Bas saw).
  const volumeRef = useRef(volume);
  useEffect(() => { volumeRef.current = volume; }, [volume]);

  // #625 — walk the candidate list (primary id + fallbacks) on YT
  // player error or never-reached-PLAYING. State is held in refs so
  // the player-creation useEffect doesn't re-run on every advance
  // (which would be functionally fine but generate spurious renders).
  // The current id is held in component state so React renders the
  // <YouTube> instance with the right videoId; the index ref tracks
  // where we are in the candidate list.
  const candidateListRef = useRef([]);
  // Build the list once per props instance: primary first, then any
  // fallbacks. Refresh whenever the parent passes a new primary id
  // (= a new show_video event from the server).
  useEffect(() => {
    candidateListRef.current = [
      youtubeId,
      ...((Array.isArray(fallbackIds) ? fallbackIds : []).filter(Boolean)),
    ].filter(Boolean);
  }, [youtubeId, fallbackIds]);
  const [activeId, setActiveId] = useState(youtubeId);
  const activeIndexRef = useRef(0);
  // Re-seed activeId when the parent passes a new primary (new turn /
  // new show_video event). Without this, a back-to-back video request
  // would keep playing the previous candidate list.
  useEffect(() => {
    activeIndexRef.current = 0;
    setActiveId(youtubeId);
  }, [youtubeId]);

  // Refs guarding the never-played timer + the "we already gave up
  // on this one" flag, so onError + timeout don't double-advance.
  const neverPlayedTimerRef = useRef(null);
  const advancingRef = useRef(false);

  // Forward declaration so onError + timeout can reference the same
  // function. Holds a closure that advances to the next candidate or
  // calls onUnavailable when exhausted.
  const advanceToNextRef = useRef(() => {});

  useEffect(() => {
    let cancelled = false;
    if (!activeId) return;
    advancingRef.current = false;

    // The advance closure: bumps the index, swaps activeId to the next
    // candidate (which re-runs this effect cleanly via the [activeId]
    // dep), or fires onUnavailable when we've run out.
    advanceToNextRef.current = (reason) => {
      if (advancingRef.current) return;  // already advancing
      advancingRef.current = true;
      if (neverPlayedTimerRef.current) {
        clearTimeout(neverPlayedTimerRef.current);
        neverPlayedTimerRef.current = null;
      }
      const list = candidateListRef.current;
      const nextIdx = activeIndexRef.current + 1;
      if (nextIdx >= list.length) {
        console.warn(
          `[VideoOverlay #625] all candidates exhausted (${list.length} tried) — ` +
          `last reason=${reason}; signalling onUnavailable`
        );
        if (typeof onUnavailable === 'function') onUnavailable(reason);
        return;
      }
      const nextId = list[nextIdx];
      console.warn(
        `[VideoOverlay #625] candidate ${activeIndexRef.current} (${list[activeIndexRef.current]}) ` +
        `failed (${reason}) — advancing to ${nextIdx}/${list.length - 1} (${nextId})`
      );
      activeIndexRef.current = nextIdx;
      setActiveId(nextId);
    };

    loadYouTubeApi().then(() => {
      if (cancelled || !containerRef.current) return;
      const inner = document.createElement('div');
      inner.id = `yt-video-player-${Math.random().toString(36).slice(2)}`;
      inner.style.width = '100%';
      inner.style.height = '100%';
      containerRef.current.appendChild(inner);
      playerRef.current = new window.YT.Player(inner.id, {
        // String '100%' tells YT to size the iframe to its container
        // rather than the default 640×360 pixels. Without this, the
        // iframe was a small box centred in our larger frame.
        width: '100%',
        height: '100%',
        videoId: activeId,
        host: 'https://www.youtube.com',
        playerVars: {
          autoplay: 1,
          controls: 0,            // hide native controls — we have our own
          disablekb: 1,
          fs: 0,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
          iv_load_policy: 3,
          origin: window.location.origin,
        },
        events: {
          onReady: (e) => {
            readyRef.current = true;
            // #755 — pin the iframe via absolute positioning instead
            // of percentage-width inside a flex container. Bas's
            // 2026-05-16 DevTools snapshot proved the previous
            // approach (three layers of `width:100%; height:100%` on
            // a flex item with `flex-basis: 0%`) didn't propagate a
            // definite size for percentage children — the iframe
            // fell back to its default 300×150 replaced-element size
            // and rendered tiny in a corner. Absolute-positioning
            // against a `position:relative` parent sidesteps the
            // flex-basis-0% percentage-resolution gotcha entirely;
            // this is the canonical CSS pattern for "iframe fills
            // arbitrary container" (YouTube/Vimeo wrappers all do
            // this). Pointer-events stay locked so user clicks can
            // never accidentally hit YouTube's UI.
            try {
              const iframe = e.target.getIframe ? e.target.getIframe() : null;
              if (iframe) {
                iframe.style.position = 'absolute';
                iframe.style.inset = '0';
                iframe.style.width = '100%';
                iframe.style.height = '100%';
                iframe.style.display = 'block';
                iframe.style.pointerEvents = 'none';
              }
            } catch (_) {}
            try { e.target.setVolume(volumeRef.current); } catch (_) {}
            // #625 — arm the never-played watchdog. Some rights-blocks
            // load the iframe successfully (onReady fires) but never
            // transition past UNSTARTED/CUED — onError isn't fired.
            // If we don't see PLAYING within NEVER_PLAYED_TIMEOUT_MS
            // we treat that as a silent block and advance.
            if (neverPlayedTimerRef.current) {
              clearTimeout(neverPlayedTimerRef.current);
            }
            neverPlayedTimerRef.current = setTimeout(() => {
              advanceToNextRef.current('never_reached_playing');
            }, NEVER_PLAYED_TIMEOUT_MS);
          },
          onStateChange: e => {
            const s = e.data;
            if (s === 1) {
              // PLAYING — cancel the never-played watchdog. The
              // candidate is good.
              if (neverPlayedTimerRef.current) {
                clearTimeout(neverPlayedTimerRef.current);
                neverPlayedTimerRef.current = null;
              }
              setIsPlaying(true);
            }
            else if (s === 2 || s === 0) setIsPlaying(false);
          },
          // #625 — YT IFrame API onError. Codes:
          //   2   invalid parameter (bad video id)
          //   5   HTML5 player error
          //   100 video not found / removed / private
          //   101 embedding disabled by owner
          //   150 same as 101 (different reason variant — typically
          //       fires for content blocks like LaLiga rights claims)
          // All five = "this candidate is broken, try the next one".
          onError: (e) => {
            advanceToNextRef.current(`yt_error_${e && e.data}`);
          },
        },
      });
    });
    return () => {
      cancelled = true;
      if (neverPlayedTimerRef.current) {
        clearTimeout(neverPlayedTimerRef.current);
        neverPlayedTimerRef.current = null;
      }
      try {
        if (playerRef.current && playerRef.current.destroy) {
          playerRef.current.destroy();
        }
      } catch (_) {}
    };
    // Only re-create the player when activeId changes (= primary swap
    // OR we advanced to a fallback). Volume is applied imperatively
    // via setVolume() in adjustVolume() and read via volumeRef in
    // onReady.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  if (!youtubeId) return null;

  const togglePlay = () => {
    const p = playerRef.current;
    if (!p || !readyRef.current) return;
    if (isPlaying) p.pauseVideo();
    else p.playVideo();
  };

  // Volume +/- buttons instead of a slider — simpler, more reliable.
  // The slider had two confirmed bugs: it appeared to scrub the video
  // timeline (Bas's earlier feedback), and clicking it shrank the
  // iframe (most recent screenshot). Buttons sidestep all of that and
  // are easier for elderly users to use accurately than a fine slider.
  const VOLUME_STEP = 10;
  const adjustVolume = (delta) => {
    const next = Math.max(0, Math.min(100, volume + delta));
    if (next === volume) return;
    setVolume(next);
    const p = playerRef.current;
    if (p && readyRef.current && p.setVolume) p.setVolume(next);
  };

  return (
    <div style={styles.backdrop}>
      {/* TV frame around the video — fills the available space above
          the control bar. */}
      <div style={styles.tvFrame}>
        <div ref={containerRef} style={styles.iframeMount} />
      </div>

      {title && <div style={styles.title}>{title}</div>}

      {/* Single control bar — Done + Play/Pause + Volume all together
          so the user finds everything in one place. Dark background
          with an orange brand accent so it reads as "AmigoMio
          controls", not a generic browser overlay. */}
      {/* #1089 — Done removed: the ActivityFrame owns the close (X).
          The control bar now carries only the content controls. */}
      <div style={styles.controlBar}>
        <button
          onClick={togglePlay}
          style={styles.playBtn}
          aria-label={isPlaying ? tr.pause : tr.play}
        >
          <span style={styles.playIcon}>{isPlaying ? '⏸' : '▶'}</span>
          <span style={styles.btnLabel}>{isPlaying ? tr.pause : tr.play}</span>
        </button>

        <div style={styles.volumeWrap}>
          <button
            onClick={() => adjustVolume(-VOLUME_STEP)}
            style={styles.volumeBtn}
            aria-label={tr.volumeDown}
            disabled={volume === 0}
          >
            <span style={styles.volumeBtnIcon}>−</span>
          </button>
          <div style={styles.volumeDisplay}>
            <span style={styles.volumeIcon}>🔊</span>
            <span style={styles.volumeNumber}>{volume}</span>
          </div>
          <button
            onClick={() => adjustVolume(VOLUME_STEP)}
            style={styles.volumeBtn}
            aria-label={tr.volumeUp}
            disabled={volume === 100}
          >
            <span style={styles.volumeBtnIcon}>+</span>
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    // #1089 — pure content: fills the ActivityFrame content slot. The
    // frame owns the full-screen positioning + z-index + close; was
    // position:fixed / inset:0 / zIndex:9998.
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    width: '100%',
    background: '#000',
    display: 'flex',
    flexDirection: 'column',
  },
  tvFrame: {
    flex: 1,
    margin: '20px 32px 14px',
    border: '4px solid #1a1a1a',
    borderRadius: '14px',
    background: '#000',
    boxShadow: '0 0 60px rgba(0, 0, 0, 0.7), inset 0 0 0 2px #2c2c2c',
    overflow: 'hidden',
    display: 'flex',
    minHeight: 0, // let flex shrink below content size
  },
  iframeMount: {
    flex: 1,
    width: '100%',
    height: '100%',
    // #755 — `position: relative` makes this the absolute-positioning
    // anchor for the YT iframe (which onReady pins via `position:
    // absolute; inset: 0`). Was `display: flex` previously; that made
    // the iframe a flex item whose `width: 100%` couldn't resolve to a
    // definite size because this div's own size comes from
    // `flex-basis: 0%` + flex-grow — replaced elements like <iframe>
    // would then fall back to their default 300×150 size and render
    // tiny in a corner of the tvFrame.
    position: 'relative',
    // Block clicks on the iframe so they don't interfere with our
    // custom controls (Bas reported the slider appeared to scrub the
    // video timeline — that was the iframe capturing clicks).
    pointerEvents: 'none',
  },
  title: {
    flexShrink: 0,
    color: '#ddd',
    padding: '6px 32px 4px',
    fontSize: '0.95rem',
    fontWeight: 500,
    textAlign: 'center',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  // Single control bar at the bottom — clear visual separation
  // from the video, with the AmigoMio brand orange as a top accent
  // so the user recognises this as our chrome (not random browser UI).
  controlBar: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 28px 22px',
    gap: '20px',
    background: 'rgba(28, 28, 30, 0.98)',
    borderTop: `3px solid ${BRAND_ORANGE}`,
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
  },
  // Done — secondary style: white outline, dark fill. Less visually
  // dominant than Play (the more frequently used action) but still
  // big and tappable.
  doneBtn: {
    minWidth: '130px',
    minHeight: '60px',
    padding: '10px 22px',
    border: `2px solid ${BRAND_ORANGE}`,
    borderRadius: '999px',
    background: 'transparent',
    color: '#fff',
    fontSize: '1.1rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    justifyContent: 'center',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  // Play — primary action, brand orange filled, slightly larger.
  playBtn: {
    minWidth: '160px',
    minHeight: '64px',
    padding: '12px 28px',
    border: 'none',
    borderRadius: '999px',
    background: BRAND_ORANGE,
    color: '#fff',
    fontSize: '1.15rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(232, 132, 90, 0.4)',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  playIcon: { fontSize: '1.5rem', lineHeight: 1 },
  btnIcon: { fontSize: '1.2rem', lineHeight: 1 },
  btnLabel: { letterSpacing: '0.02em' },
  volumeWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#fff',
  },
  volumeBtn: {
    width: '54px',
    height: '54px',
    borderRadius: '50%',
    border: `2px solid ${BRAND_ORANGE}`,
    background: 'transparent',
    color: '#fff',
    fontSize: '1.6rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: 0,
  },
  volumeBtnIcon: {
    lineHeight: 1,
  },
  // Always-visible numeric volume so the user knows where they are.
  volumeDisplay: {
    minWidth: '78px',
    height: '54px',
    padding: '0 12px',
    borderRadius: '999px',
    background: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  volumeIcon: {
    fontSize: '1.1rem',
  },
  volumeNumber: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#fff',
    minWidth: '28px',
    textAlign: 'center',
  },
};
